// ============================================================================
// PÁGINA: COSMÉTÓLOGO INTERNACIONAL - VISUAL PREMIUM (SEM BLOCO LARANJA GIGANTE)
// ============================================================================
// Arquivo: src/pages/servicos/CosmetologoInternacional.tsx
// Descrição: Página informativa sobre Cosmetologia Internacional
// ============================================================================

import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Globe, Award, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";

const CosmetologoInternacional: React.FC = () => {
  const areasDeAtuacao = [
    "Estética facial e corporal",
    "Tricologia e cuidados capilares",
    "Corte Feminino e Masculino",
    "Maquiagem profissional",
    "Técnicas cosméticas avançadas",
    "Cuidados pré e pós-procedimentos dermatológicos",
    "Saúde e bem-estar integrados à beleza",
    "Biossegurança",
    "Gestão e Marketing",
  ];

  const requisitos = [
    "Ser Filiado à Federação com comprovação de prática profissional e/ou formação técnica na área da beleza e estética",
    "Realizar curso complementar (quando necessário) com conteúdos exigidos por instituições internacionais",
    "Solicitar validação do currículo e emissão do Diploma Internacional, com carga horária compatível e documentação comprobatória",
    "Obter a carteira profissional internacional da Federação, com número de registro e QR Code para verificação",
  ];

  const apoioFederacao = [
    "Avaliação curricular",
    "Complementação de Estudos",
    "Emissão de documentos oficiais",
    "Traduções juramentadas",
    "Suporte de instituições e advogados de imigração parceiros no exterior",
  ];

  const beneficiosEscolas = [
    "Realizem processos para licenciamento estadual",
    "Recebam complementação de estudos conforme o padrão internacional",
    "Participem de programas de intercâmbio e capacitação",
    "Tenham acesso a cursos híbridos (online + prático presencial) com tutoria internacional",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30 text-gray-900">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-amber-950 to-gray-950" />
        <div className="absolute inset-0 opacity-15">
          <div className="absolute -top-24 -left-24 w-[520px] h-[520px] bg-amber-500 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-[520px] h-[520px] bg-orange-500 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 relative z-10 py-16 md:py-24">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col items-center text-center">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8 shadow-[0_0_0_1px_rgba(255,255,255,.05)]">
                {/* ✅ Parcerias (icon6) */}
                <img
                  src="/icons/icon6.png"
                  alt="Certificação Internacional"
                  className="w-6 h-6 object-contain"
                />
                <span className="text-xs md:text-sm font-bold text-amber-100 uppercase tracking-wider">
                  Certificação Internacional
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white">
                Cosmétólogo Internacional
              </h1>

              <p className="mt-6 text-xl md:text-2xl font-bold text-amber-100 leading-relaxed max-w-3xl">
                Transforme sua carreira em um passaporte para o mundo.
              </p>

              <p className="mt-6 text-base md:text-lg text-white/85 leading-relaxed max-w-3xl">
                A Federação oferece o respaldo necessário para que o profissional da beleza atue legalmente como
                Cosmetólogo Internacional nos países que regulamentam a profissão.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/filiacao"
                  className="inline-flex items-center justify-center gap-3 px-7 py-3.5 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all"
                >
                  <span>Torne-se um Filiado</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>

                <a
                  href="#requisitos"
                  className="inline-flex items-center justify-center gap-3 px-7 py-3.5 bg-white/10 text-white font-bold rounded-xl border border-white/20 backdrop-blur-md hover:bg-white/15 transition-all"
                >
                  <span>Ver requisitos</span>
                  <ShieldCheck className="w-5 h-5" />
                </a>
              </div>

              {/* ✅ (Opcional) ainda usa lucide no hero cards — não é bloco pedido */}
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-4xl">
                {[
                  { icon: <Globe className="w-5 h-5" />, title: "Reconhecimento", desc: "Atuação conforme legislação local" },
                  { icon: <Award className="w-5 h-5" />, title: "Respaldo", desc: "Documentação e validação" },
                  { icon: <Sparkles className="w-5 h-5" />, title: "Carreira", desc: "Mais possibilidades e serviços" },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="rounded-2xl bg-white/10 border border-white/15 backdrop-blur-md px-5 py-4 text-left"
                  >
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center">
                        {item.icon}
                      </div>
                      <div className="font-extrabold">{item.title}</div>
                    </div>
                    <div className="mt-2 text-sm text-white/75">{item.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </section>

      {/* O QUE É */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-amber-100/80 backdrop-blur-sm rounded-full border border-amber-300/60 mb-6 shadow-sm">
                <img src="/icons/icone2.png" alt="Informação" className="w-6 h-6 object-contain" />
                <span className="text-xs md:text-sm font-bold text-amber-900 uppercase tracking-wider">
                  Sobre o Título
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900">
                O que é Cosmetologia Internacional?
              </h2>
            </div>

            <div className="bg-white rounded-3xl shadow-[0_25px_60px_-35px_rgba(0,0,0,.35)] border border-amber-100/60 p-7 md:p-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                A <strong className="text-amber-600">Cosmetologia Internacional</strong> é a ciência e a prática estética
                aplicada de forma ampla e regulamentada em diferentes países do mundo. Envolve conhecimentos técnicos,
                científicos e legais para a atuação profissional em áreas como:
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-10">
                {areasDeAtuacao.map((area, idx) => (
                  <div
                    key={idx}
                    className="group flex items-center gap-3 rounded-2xl p-4 border border-amber-100 bg-gradient-to-br from-amber-50 to-white hover:shadow-md transition-all"
                  >
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-gray-900 font-semibold">{area}</span>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl p-6 md:p-8 border border-amber-200 bg-gradient-to-br from-amber-50/70 to-orange-50/40">
                <p className="text-lg text-gray-700 leading-relaxed">
                  O título de <strong className="text-amber-600">Cosmetólogo Internacional</strong> é reconhecido em
                  diversos países como uma qualificação superior ao de Cabeleireiro, permitindo ao profissional realizar
                  uma gama mais ampla de serviços e atuar de forma legal, conforme a legislação local. A Federação orienta
                  seus Filiados conforme os requisitos de cada país.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REQUISITOS */}
      <section id="requisitos" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-amber-100/80 backdrop-blur-sm rounded-full border border-amber-300/60 mb-6 shadow-sm">
                <img src="/icons/icone16.png" alt="Requisitos" className="w-6 h-6 object-contain" />
                <span className="text-xs md:text-sm font-bold text-amber-900 uppercase tracking-wider">
                  Como Obter
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4">
                Requisitos para Reconhecimento
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Para ser reconhecido como Cosmetólogo Internacional pela Federação, o profissional precisa:
              </p>
            </div>

            <div className="space-y-5">
              {requisitos.map((req, idx) => (
                <div
                  key={idx}
                  className="rounded-3xl p-6 md:p-7 border border-amber-100 bg-gradient-to-br from-white to-amber-50/40 shadow-[0_18px_45px_-35px_rgba(0,0,0,.45)] hover:shadow-[0_25px_60px_-40px_rgba(0,0,0,.55)] transition-all"
                >
                  <div className="flex gap-5 items-start">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-600 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-md">
                      <span className="text-white font-black text-lg">{idx + 1}</span>
                    </div>

                    <div className="flex-1">
                      <p className="text-gray-900 font-semibold text-lg leading-relaxed">{req}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mini CTA */}
            <div className="mt-10 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-7 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
              <div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-600 to-orange-600 flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div className="font-black text-gray-900 text-lg">Pronto para dar o próximo passo?</div>
                </div>
                <p className="mt-2 text-gray-700">
                  Inicie o processo de filiação e receba orientação para validar sua atuação conforme o país desejado.
                </p>
              </div>

              <Link
                to="/filiacao"
                className="inline-flex items-center justify-center gap-3 px-7 py-3.5 bg-gray-900 text-white font-bold rounded-xl shadow hover:shadow-lg hover:scale-[1.02] transition-all"
              >
                <span>Quero me filiar</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* APOIO DA FEDERAÇÃO (✅ trocado para icon4.png) */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-gray-900 text-white rounded-full border border-gray-900/10 mb-6 shadow-sm">
                {/* ✅ Suporte (icon4) */}
                <img
                  src="/icons/icon4.png"
                  alt="Suporte Completo"
                  className="w-5 h-5 object-contain"
                />
                <span className="text-xs md:text-sm font-bold uppercase tracking-wider">Suporte Completo</span>
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900">
                A Federação oferece apoio completo com:
              </h2>

              <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                Um caminho claro, com suporte documental e orientação para ampliar sua atuação internacional.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {apoioFederacao.map((item, idx) => (
                <div
                  key={idx}
                  className="group bg-white rounded-3xl border border-amber-100/70 p-7 shadow-[0_20px_55px_-40px_rgba(0,0,0,.45)] hover:shadow-[0_28px_70px_-45px_rgba(0,0,0,.55)] transition-all"
                >
                  <div className="flex items-center gap-4">
                    {/* ✅ antes era bloco laranja com CheckCircle, agora é foto do icon4 */}
                    <div className="relative w-12 h-12 flex-shrink-0">
                      <div className="absolute inset-0 bg-amber-400/25 rounded-2xl blur-lg" />
                      <div className="relative z-10 w-12 h-12 rounded-2xl bg-white border border-amber-100 shadow-md flex items-center justify-center overflow-hidden">
                        <img
                          src="/icons/icon4.png"
                          alt="Suporte"
                          className="w-8 h-8 object-contain"
                        />
                      </div>
                    </div>

                    <div className="font-extrabold text-gray-900 text-lg leading-snug">{item}</div>
                  </div>

                  <div className="mt-5 h-px w-full bg-gradient-to-r from-transparent via-amber-200/70 to-transparent" />

                  <p className="mt-4 text-gray-700 leading-relaxed">
                    Atendimento orientado para facilitar sua regularização e emissão de documentos necessários para
                    comprovação profissional.
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-3xl border border-gray-900/10 bg-gradient-to-br from-gray-900 to-gray-950 text-white p-7 md:p-10 overflow-hidden relative">
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl" />

              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div>
                  <div className="flex items-center gap-3">
                    {/* ✅ antes era ShieldCheck, agora icon4 */}
                    <div className="w-10 h-10 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center overflow-hidden">
                      <img src="/icons/icon4.png" alt="Suporte" className="w-6 h-6 object-contain" />
                    </div>
                    <div className="font-black text-xl">Processo com orientação</div>
                  </div>
                  <p className="mt-3 text-white/80 max-w-2xl">
                    Você não precisa “adivinhar” os passos: a Federação direciona conforme o país e a exigência local,
                    com suporte para documentação e validação.
                  </p>
                </div>

                <Link
                  to="/filiacao"
                  className="inline-flex items-center justify-center gap-3 px-7 py-3.5 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all"
                >
                  <span>Iniciar filiação</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ESCOLAS INTERNACIONAIS (✅ selo usa icon6 e cards usam icon6 no lugar do Globe) */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-amber-100/80 backdrop-blur-sm rounded-full border border-amber-300/60 mb-6 shadow-sm">
                {/* ✅ Parcerias (icon6) */}
                <img src="/icons/icon6.png" alt="Parcerias Internacionais" className="w-6 h-6 object-contain" />
                <span className="text-xs md:text-sm font-bold text-amber-900 uppercase tracking-wider">
                  Parcerias Internacionais
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900">
                Escolas Internacionais Parceiras
              </h2>
            </div>

            <div className="bg-white rounded-3xl shadow-[0_25px_60px_-35px_rgba(0,0,0,.35)] border border-amber-100/60 p-7 md:p-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                A Federação mantém alianças estratégicas com escolas e centros de beleza nos{" "}
                <strong className="text-amber-600">EUA</strong>,{" "}
                <strong className="text-amber-600">Portugal</strong> e{" "}
                <strong className="text-amber-600">América Latina</strong>, permitindo que nossos profissionais:
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {beneficiosEscolas.map((beneficio, idx) => (
                  <div
                    key={idx}
                    className="rounded-2xl p-6 border border-amber-200 bg-gradient-to-br from-amber-50 to-white hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start gap-4">
                      {/* ✅ antes era bloco laranja com Globe, agora icon6 */}
                      <div className="w-11 h-11 rounded-2xl bg-white border border-amber-100 shadow-md flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <img src="/icons/icon6.png" alt="Parcerias" className="w-7 h-7 object-contain" />
                      </div>

                      <p className="text-gray-900 font-semibold leading-relaxed flex-1">{beneficio}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/70 to-orange-50/40 p-6 md:p-8">
                <p className="text-gray-700 leading-relaxed">
                  Observação: os requisitos variam de acordo com o país. A Federação orienta o caminho ideal para sua
                  validação e atuação legal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FECHO */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-amber-950 to-gray-950" />
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[680px] h-[680px] bg-amber-500 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="relative mb-8">
              <div className="absolute top-0 left-0 text-amber-300 opacity-25 text-8xl font-serif leading-none">"</div>
              <div className="relative z-10 pt-10">
                <p className="text-2xl md:text-4xl font-extrabold leading-relaxed">
                  A beleza não tem fronteiras. Com o respaldo certo, você também pode conquistar o mundo.
                </p>
              </div>
              <div className="absolute bottom-0 right-0 text-amber-300 opacity-25 text-8xl font-serif leading-none">"</div>
            </div>

            <div className="h-1 w-24 bg-gradient-to-r from-amber-400 to-orange-400 mx-auto mb-8" />

            <Link
              to="/filiacao"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all"
            >
              <span>Torne-se um Filiado</span>
              <ArrowRight className="w-5 h-5" />
            </Link>

            <p className="mt-6 text-white/75 text-sm">
              Dúvidas? Entre em contato e receba orientação sobre o caminho ideal para sua atuação internacional.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CosmetologoInternacional;
